import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ScanFace, CheckCircle2, XCircle, ShieldAlert, Calendar } from "lucide-react";
import api from "@/lib/api";
import { Card, PageHeader, Badge, Field, Input, Select } from "@/components/ui-kit";
import { usePagination } from "@/hooks/usePagination";

/**
 * Iter v30 — Face Attempt Audit Logs viewer.
 * Admin/HR can audit all face verification attempts (success/failure/override).
 * Useful for: investigating disputes, detecting impersonation attempts,
 * confirming override usage.
 */

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function FaceAuditLogs() {
  const [mode, setMode] = useState("month"); // month | day
  const [date, setDate] = useState(todayStr());
  const [employeeId, setEmployeeId] = useState("");
  const [kind, setKind] = useState("");
  const [successFilter, setSuccessFilter] = useState(""); // "" | "true" | "false"
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (mode === "day") params.date = date;
      else params.month = date.slice(0, 7);
      if (employeeId) params.employee_id = employeeId;
      if (kind) params.kind = kind;
      if (successFilter !== "") params.success = successFilter;
      const r = await api.get("/face-attempts", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat audit log");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => setEmployees([]));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, date, employeeId, kind, successFilter]);

  const { pageItems, Pagination } = usePagination(data.rows || [], { perPage: 25 });

  const fmt = (ts) => {
    if (!ts) return "—";
    try {
      const d = new Date(ts);
      return d.toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
    } catch { return ts; }
  };

  const periodLabel = useMemo(() => {
    if (mode === "day") return new Date(date + "T00:00").toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    return new Date(date + "T00:00").toLocaleDateString("id-ID", { month: "long", year: "numeric" });
  }, [mode, date]);

  const threshold = data.threshold || 0.4;

  return (
    <div className="space-y-6" data-testid="face-audit-page">
      <PageHeader overline="Admin · HR" title="Audit Verifikasi Wajah"
        description="Semua percobaan verifikasi wajah (sukses, gagal, override) tercatat di sini untuk keperluan investigasi & kepatuhan." />

      <Card className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex border border-stone-300">
            {[{ k: "month", l: "Bulan Ini" }, { k: "day", l: "Hari" }].map((t) => (
              <button key={t.k} onClick={() => setMode(t.k)}
                className={`px-3 py-1.5 text-xs uppercase tracking-wider ${mode === t.k ? "bg-terra-500 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
                data-testid={`face-audit-mode-${t.k}`}>{t.l}</button>
            ))}
          </div>
          <Badge tone="stone"><Calendar className="h-3 w-3 inline" /> {periodLabel}</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label={mode === "day" ? "Tanggal" : "Bulan (pilih tanggal apapun)"}>
            <Input type={mode === "day" ? "date" : "month"} value={mode === "day" ? date : date.slice(0, 7)}
              onChange={(e) => setDate(mode === "day" ? e.target.value : e.target.value + "-15")} data-testid="face-audit-date" />
          </Field>
          <Field label="Karyawan">
            <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} data-testid="face-audit-emp-filter">
              <option value="">— Semua —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </Select>
          </Field>
          <Field label="Jenis">
            <Select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="face-audit-kind-filter">
              <option value="">— Semua —</option>
              <option value="clock-in">Clock-In</option>
              <option value="clock-out">Clock-Out</option>
            </Select>
          </Field>
          <Field label="Hasil">
            <Select value={successFilter} onChange={(e) => setSuccessFilter(e.target.value)} data-testid="face-audit-result-filter">
              <option value="">— Semua —</option>
              <option value="true">Sukses</option>
              <option value="false">Gagal</option>
            </Select>
          </Field>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Total Percobaan</div><div className="text-2xl font-display font-bold text-stone-900 mt-1" data-testid="face-audit-total">{data.summary?.total || 0}</div></Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Sukses</div><div className="text-2xl font-display font-bold text-emerald-700 mt-1" data-testid="face-audit-success">{data.summary?.successes || 0}</div></Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Gagal</div><div className="text-2xl font-display font-bold text-red-700 mt-1" data-testid="face-audit-failures">{data.summary?.failures || 0}</div></Card>
        <Card className="p-3 text-center"><div className="text-[10px] uppercase tracking-widest text-stone-500">Override Dipakai</div><div className="text-2xl font-display font-bold text-amber-700 mt-1" data-testid="face-audit-overrides">{data.summary?.overrides || 0}</div></Card>
      </div>

      <Card className="p-3 text-xs text-stone-600 bg-stone-50">
        <ShieldAlert className="h-3.5 w-3.5 inline text-stone-500" /> Threshold ketat: <b className="font-mono">{threshold}</b>. Jarak ≤ threshold → sukses; di atas → gagal.
      </Card>

      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500">
            <tr>
              <th className="text-left px-3 py-3">Waktu</th>
              <th className="text-left px-3 py-3">Karyawan</th>
              <th className="text-center px-3 py-3">Jenis</th>
              <th className="text-right px-3 py-3">Jarak</th>
              <th className="text-center px-3 py-3">Hasil</th>
              <th className="text-center px-3 py-3">Override</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {loading && <tr><td colSpan={6} className="px-3 py-12 text-center text-xs text-stone-400">Memuat…</td></tr>}
            {!loading && pageItems.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-12 text-center text-xs text-stone-400">Belum ada catatan verifikasi wajah pada periode ini.</td></tr>
            )}
            {!loading && pageItems.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50/40" data-testid={`face-audit-row-${r.id}`}>
                <td className="px-3 py-2.5 font-mono text-xs text-stone-700">{fmt(r.ts)}</td>
                <td className="px-3 py-2.5 font-medium text-stone-900">{r.employee_name}</td>
                <td className="px-3 py-2.5 text-center">
                  <Badge tone="stone">{r.kind === "clock-in" ? "masuk" : "keluar"}</Badge>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">
                  <span className={r.face_distance > threshold ? "text-red-700" : "text-emerald-700"}>{r.face_distance}</span>
                  <span className="text-stone-400"> / {r.threshold || threshold}</span>
                </td>
                <td className="px-3 py-2.5 text-center">
                  {r.success
                    ? <Badge tone="emerald"><CheckCircle2 className="h-3 w-3 inline" /> sukses</Badge>
                    : <Badge tone="red"><XCircle className="h-3 w-3 inline" /> gagal</Badge>}
                </td>
                <td className="px-3 py-2.5 text-center">
                  {r.force_override ? <Badge tone="amber">override</Badge> : <span className="text-stone-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {Pagination}
      </Card>
    </div>
  );
}
